import type { JsonType } from 'posthog-js';
import { type ReactNode, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { LoadingIndicator } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { AnalyticsProvider } from '@eluve/posthog-react';
import {
  useTenantIdFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { appConfig } from './config';
import * as featureFlagCache from './featureFlagCache';

const FEATURE_FLAGS_TIMEOUT_MS = 1000;

interface SessionBootstrappedAnalyticsProviderProps {
  children: ReactNode;
  apiHost: string;
}

interface ProviderState {
  loaded: boolean;
  flags: featureFlagCache.FeatureFlags | undefined;
  payloads: featureFlagCache.FeatureFlagPayloads | undefined;
}

export const SessionBootstrappedAnalyticsProvider = ({
  children,
  apiHost,
}: SessionBootstrappedAnalyticsProviderProps) => {
  const apiClient = useApiClient();
  const logger = useNamedLogger('FeatureFlagBootstrap');
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromSession() ?? 'no-tenant';

  const [result, setResult] = useState<ProviderState>(() => {
    const cached = featureFlagCache.read(userId, tenantId);
    return cached
      ? { loaded: true, flags: cached.flags, payloads: cached.payloads }
      : { loaded: false, flags: undefined, payloads: undefined };
  });

  useEffect(() => {
    const abortController = new AbortController();
    // Re-read the cache at effect time (not a ref captured at first mount) so
    // the warm-load vs cold-load decision matches the current user/tenant
    // even if those identities change mid-mount.
    const cached = featureFlagCache.read(userId, tenantId);
    const startedFromCache = cached !== null;

    const fetchFlags = async (): Promise<
      featureFlagCache.FeatureFlagsWithPayloads | undefined
    > => {
      const response = await apiClient.auth.getFeatureFlags({
        // Embed user/tenant in the URL so the browser HTTP cache key is
        // naturally per-user, which lets us drop `Vary: Cookie` (whose
        // byte-for-byte cookie matching defeats hits when any unrelated
        // cookie changes).
        query: { u: userId, t: tenantId },
        fetchOptions: { signal: abortController.signal },
      });
      if (response.status === 200) {
        return {
          flags: response.body.featureFlags,
          payloads: (response.body.featureFlagPayloads ?? {}) as Record<
            string,
            JsonType
          >,
        };
      }
      return undefined;
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleSuccess = (
      featureFlags: featureFlagCache.FeatureFlagsWithPayloads | undefined,
    ) => {
      if (featureFlags?.flags) {
        featureFlagCache.write(userId, tenantId, featureFlags);
      }
      setResult({
        loaded: true,
        flags: featureFlags?.flags,
        payloads: featureFlags?.payloads,
      });
    };

    const handleMissOrTimeout = (
      reason: 'timeout' | 'error',
      error?: unknown,
    ) => {
      if (startedFromCache) {
        if (reason === 'error') {
          logger.warn('Background feature flag refresh failed', { error });
        }
        return;
      }
      if (reason === 'timeout') {
        logger.warn(
          'Feature flag fetch timed out, falling back to client-side defaults',
        );
      } else {
        logger.warn(
          'Feature flag fetch failed, falling back to client-side defaults',
          { error },
        );
      }
      setResult({ loaded: true, flags: undefined, payloads: undefined });
    };

    if (startedFromCache) {
      fetchFlags()
        .then((flags) => {
          if (abortController.signal.aborted) return;
          if (flags === undefined) {
            // Non-200 response — preserve cached flags and skip the
            // setResult so the background refresh can't clobber them.
            return;
          }
          handleSuccess(flags);
        })
        .catch((error) => {
          if (abortController.signal.aborted || error?.name === 'AbortError')
            return;
          handleMissOrTimeout('error', error);
        });
    } else {
      // Timeout only ungates render with defaults; the fetch keeps running so real flags still apply late (via AnalyticsProvider re-render).
      timeoutId = setTimeout(() => {
        if (abortController.signal.aborted) return;
        handleMissOrTimeout('timeout');
      }, FEATURE_FLAGS_TIMEOUT_MS);

      fetchFlags()
        .then((flags) => {
          if (timeoutId) clearTimeout(timeoutId);
          if (abortController.signal.aborted) return;
          handleSuccess(flags);
        })
        .catch((error) => {
          if (timeoutId) clearTimeout(timeoutId);
          if (abortController.signal.aborted || error?.name === 'AbortError')
            return;
          handleMissOrTimeout('error', error);
        });
    }

    return () => {
      abortController.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [apiClient, logger, userId, tenantId]);

  if (!result.loaded) return <LoadingIndicator size="xl" />;

  return (
    <AnalyticsProvider
      apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
      apiHost={apiHost}
      bootstrapFlags={result.flags}
      bootstrapFlagPayloads={result.payloads}
    >
      {children}
    </AnalyticsProvider>
  );
};
